import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Form, Modal, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import { ArrowClockwise, Clock, Play, Pause, Gear, Calendar, Plus, Trash, Pencil } from 'react-bootstrap-icons';
import { useAccount } from '../contexts/AccountContext';
import { useGetEmailAccountsQuery, useGetUserPreferencesQuery } from '../services/api';
import EMMSectionHeader from './EMMSectionHeader';
import HelpSidebar from './HelpSidebar';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleStringInTimezone,
} from '../utils/tenantTimezone';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';

const AccountRotationScheduling = () => {
  const { selectedAccount, isAllAccountsSelected, isLoading } = useAccount();
  const [rotationSchedules, setRotationSchedules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [showHelpSidebar, setShowHelpSidebar] = useState(false);

  const [newSchedule, setNewSchedule] = useState({
    name: '',
    description: '',
    enabled: true,
    rotationType: 'round-robin', // round-robin, time-based, volume-based, health-based
    interval: 24, // hours
    volumeThreshold: 1000,
    healthThreshold: 70,
    selectedAccounts: [],
    timezone: 'UTC',
    startTime: '09:00',
    endTime: '17:00',
    daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  });

  // No mock data - show empty state until real API is connected

  // Fetch real email accounts from API
  const { 
    data: emailAccountsData,
    isLoading: emailAccountsLoading 
  } = useGetEmailAccountsQuery();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );

  // Transform email accounts data to format needed for rotation schedules
  const emailAccounts = useMemo(() => {
    const apiAccounts = emailAccountsData?.data?.emailAccounts || [];
    
    // Filter by selected account if not "All Accounts"
    let filteredAccounts = apiAccounts;
    if (!isAllAccountsSelected && selectedAccount && selectedAccount.id !== 'all') {
      const accountDomains = selectedAccount.domains || [];
      const accountDomainNames = accountDomains.map(d => d.toLowerCase());
      filteredAccounts = apiAccounts.filter(account => {
        const accountDomain = (account.domain || account.email?.split('@')[1])?.toLowerCase();
        return accountDomain && accountDomainNames.includes(accountDomain);
      });
    }
    
    // Transform to format expected by the component
    return filteredAccounts.map((account, index) => {
      // Get full email address - priority: fullEmail > email > username@domain
      const fullEmail = account.fullEmail || account.email || `${account.username || account.email?.split('@')[0]}@${account.domain || account.email?.split('@')[1]}`;
      
      return {
        id: fullEmail || `account-${index}`,
        name: account.username || account.email?.split('@')[0] || 'Unknown',
        email: fullEmail,
        domain: account.domain || account.email?.split('@')[1] || 'Unknown',
        status: account.status || 'active',
        healthScore: 95 // Default health score (can be enhanced later)
      };
    });
  }, [emailAccountsData, selectedAccount, isAllAccountsSelected]);

  // No mock data - show empty state until real API is connected
  useEffect(() => {
    setLoading(true); // Show loading when account changes

    // Simulate loading delay
    const timer = setTimeout(() => {
      // No mock data - show empty state until real API is connected
      setRotationSchedules([]);
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [selectedAccount.id, isAllAccountsSelected]);

  // Update accounts when email accounts data changes
  useEffect(() => {
    setAccounts(emailAccounts);
  }, [emailAccounts]);

  const getRotationTypeBadge = (type) => {
    switch (type) {
      case 'round-robin':
        return <Badge bg="success">Round Robin</Badge>;
      case 'time-based':
        return <Badge bg="primary">Time-Based</Badge>;
      case 'volume-based':
        return <Badge bg="info">Volume-Based</Badge>;
      case 'health-based':
        return <Badge bg="warning">Health-Based</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">Active</Badge>;
      case 'paused':
        return <Badge bg="warning">Paused</Badge>;
      case 'error':
        return <Badge bg="danger">Error</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getSuccessRate = (schedule) => {
    if (schedule.totalRuns === 0) return 0;
    return Math.round((schedule.successfulRuns / schedule.totalRuns) * 100);
  };

  const handleCreateSchedule = () => {
    setLoading(true);
    // TODO: Implement schedule creation
    setTimeout(() => {
      const newScheduleData = {
        id: rotationSchedules.length + 1,
        ...newSchedule,
        lastRun: null,
        nextRun: newSchedule.enabled ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() : null,
        totalRuns: 0,
        successfulRuns: 0,
        status: newSchedule.enabled ? 'active' : 'paused'
      };
      setRotationSchedules([...rotationSchedules, newScheduleData]);
      setLoading(false);
      setShowCreateModal(false);
      setNewSchedule({
        name: '',
        description: '',
        enabled: true,
        rotationType: 'round-robin',
        interval: 24,
        volumeThreshold: 1000,
        healthThreshold: 70,
        selectedAccounts: [],
        timezone: 'UTC',
        startTime: '09:00',
        endTime: '17:00',
        daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      });
    }, 2000);
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setShowEditModal(true);
  };

  const handleUpdateSchedule = () => {
    if (!editingSchedule) return;
    
    setLoading(true);
    // TODO: Implement schedule update API call
    setTimeout(() => {
      setRotationSchedules(prev => 
        prev.map(s => 
          s.id === editingSchedule.id 
            ? { 
                ...s, 
                ...editingSchedule,
                status: editingSchedule.enabled ? 'active' : 'paused'
              }
            : s
        )
      );
      setLoading(false);
      setShowEditModal(false);
      setEditingSchedule(null);
      alert(`✅ Rotation schedule "${editingSchedule.name}" has been updated successfully!`);
    }, 1000);
  };

  const handleToggleSchedule = (scheduleId) => {
    const schedule = rotationSchedules.find(s => s.id === scheduleId);
    if (!schedule) {
      alert('Schedule not found');
      return;
    }
    
    const newEnabled = !schedule.enabled;
    const action = newEnabled ? 'enable' : 'pause';
    
    if (!window.confirm(`${action === 'enable' ? 'Enable' : 'Pause'} rotation schedule "${schedule.name}"?`)) {
      return;
    }
    
    setRotationSchedules(prev => 
      prev.map(s => 
        s.id === scheduleId 
          ? { 
              ...s, 
              enabled: newEnabled,
              status: newEnabled ? 'active' : 'paused'
            }
          : s
      )
    );
    
    alert(`✅ Schedule "${schedule.name}" has been ${newEnabled ? 'enabled' : 'paused'}.`);
  };

  const handleDeleteSchedule = (scheduleId) => {
    const schedule = rotationSchedules.find(s => s.id === scheduleId);
    if (!schedule) {
      alert('Schedule not found');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete rotation schedule "${schedule.name}"?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setRotationSchedules(prev => prev.filter(s => s.id !== scheduleId));
    alert(`✅ Rotation schedule "${schedule.name}" has been deleted.`);
  };

  const handleRunNow = (scheduleId) => {
    const schedule = rotationSchedules.find(s => s.id === scheduleId);
    if (!schedule) {
      alert('Schedule not found');
      return;
    }
    
    if (!schedule.enabled) {
      if (!window.confirm(`Schedule "${schedule.name}" is currently paused. Enable it and run now?`)) {
        return;
      }
      // Enable the schedule first
      handleToggleSchedule(scheduleId);
    }
    
    // Show confirmation
    if (!window.confirm(`Run rotation schedule "${schedule.name}" now?\n\nThis will immediately rotate to the next account in the rotation.`)) {
      return;
    }
    
    // Update the schedule with run information
    setRotationSchedules(prev => 
      prev.map(s => 
        s.id === scheduleId 
          ? { 
              ...s, 
              lastRun: new Date().toISOString(),
              totalRuns: (s.totalRuns || 0) + 1,
              successfulRuns: (s.successfulRuns || 0) + 1,
              status: 'active'
            }
          : s
      )
    );
    
    alert(`✅ Rotation schedule "${schedule.name}" executed successfully!\n\nThe rotation has been applied and the next account is now active.`);
  };

  // Show loading spinner while data is loading
  if (loading) {
    return (
      <Container className="py-4" style={{ position: 'relative', minHeight: '400px' }}>
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <div className="text-center">
            <ArrowClockwise size={32} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
            <div className="text-muted">Loading rotation schedules...</div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <EMMSectionHeader
        title="Account Rotation Scheduling"
        showHelp
        onHelpClick={() => setShowHelpSidebar(true)}
      />
      <Container className="py-3" style={{ position: 'relative' }}>
      {/* Action bar - same style as other pages */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button
          variant="primary"
          size="sm"
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={() => setShowCreateModal(true)}
        >
          <Plus className="me-1" size={14} />
          Schedule
        </Button>
        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
          {rotationSchedules.length} schedule{rotationSchedules.length !== 1 ? 's' : ''}
        </span>
      </div>
      {/* Loading Overlay */}
      {isLoading && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <div className="text-center">
            <ArrowClockwise size={32} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
            <div className="text-muted">Switching to {selectedAccount.alias}...</div>
          </div>
        </div>
      )}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="text-muted mb-0">Automate account rotation based on time, volume, or health metrics</p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-success">Active Schedules</h5>
              <h3>{rotationSchedules.filter(s => s.enabled).length}</h3>
              <small className="text-muted">Running</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-warning">Paused Schedules</h5>
              <h3>{rotationSchedules.filter(s => !s.enabled).length}</h3>
              <small className="text-muted">Inactive</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-info">Total Rotations</h5>
              <h3>{rotationSchedules.reduce((sum, s) => sum + s.totalRuns, 0)}</h3>
              <small className="text-muted">Completed</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h5 className="text-primary">Success Rate</h5>
              <h3>
                {rotationSchedules.length > 0 
                  ? Math.round(rotationSchedules.reduce((sum, s) => sum + getSuccessRate(s), 0) / rotationSchedules.length)
                  : 0}%
              </h3>
              <small className="text-muted">Average</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Rotation Schedules Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Rotation Schedules ({rotationSchedules.length})</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Schedule Name</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Accounts</th>
                    <th>Next Run</th>
                    <th>Success Rate</th>
                    <th>Total Runs</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rotationSchedules.map((schedule) => (
                    <tr key={schedule.id}>
                      <td>
                        <div>
                          <strong>{schedule.name}</strong>
                          <div className="text-muted small">{schedule.description}</div>
                        </div>
                      </td>
                      <td>{getRotationTypeBadge(schedule.rotationType)}</td>
                      <td>{getStatusBadge(schedule.status)}</td>
                      <td>
                        <Badge bg="info">
                          {schedule.selectedAccounts.length} accounts
                        </Badge>
                      </td>
                      <td>
                        <div className="small">
                          {schedule.nextRun 
                            ? toLocaleStringInTimezone(schedule.nextRun, 'en-US', {}, tenantTimezone)
                            : 'Manual trigger required'
                          }
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <ProgressBar
                            now={getSuccessRate(schedule)}
                            variant={getSuccessRate(schedule) >= 90 ? 'success' : getSuccessRate(schedule) >= 70 ? 'warning' : 'danger'}
                            style={{ width: '60px', height: '8px' }}
                            className="me-2"
                          />
                          <span className="fw-bold">{getSuccessRate(schedule)}%</span>
                        </div>
                      </td>
                      <td>
                        <span className="fw-bold">{schedule.totalRuns}</span>
                        <div className="text-muted small">
                          {schedule.successfulRuns} successful
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant={schedule.enabled ? 'outline-warning' : 'outline-success'}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleSchedule(schedule.id);
                            }}
                            title={schedule.enabled ? 'Pause Schedule' : 'Enable Schedule'}
                          >
                            {schedule.enabled ? <Pause /> : <Play />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRunNow(schedule.id);
                            }}
                            title="Run Schedule Now"
                          >
                            <Clock />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditSchedule(schedule);
                            }}
                            title="Edit Schedule"
                          >
                            <Pencil />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSchedule(schedule.id);
                            }}
                            title="Delete Schedule"
                          >
                            <Trash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Schedule Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Rotation Schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Schedule Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Daily Marketing Rotation"
                    value={newSchedule.name}
                    onChange={(e) => setNewSchedule({...newSchedule, name: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Rotation Type</Form.Label>
                  <Form.Select
                    value={newSchedule.rotationType}
                    onChange={(e) => setNewSchedule({...newSchedule, rotationType: e.target.value})}
                  >
                    <option value="round-robin">Round Robin</option>
                    <option value="time-based">Time-Based</option>
                    <option value="volume-based">Volume-Based</option>
                    <option value="health-based">Health-Based</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Describe the purpose of this rotation schedule"
                value={newSchedule.description}
                onChange={(e) => setNewSchedule({...newSchedule, description: e.target.value})}
              />
            </Form.Group>
            {newSchedule.rotationType === 'time-based' && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rotation Interval (hours)</Form.Label>
                    <Form.Control
                      type="number"
                      value={newSchedule.interval}
                      onChange={(e) => setNewSchedule({...newSchedule, interval: parseInt(e.target.value)})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Timezone</Form.Label>
                    <Form.Select
                      value={newSchedule.timezone}
                      onChange={(e) => setNewSchedule({...newSchedule, timezone: e.target.value})}
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}
            {newSchedule.rotationType === 'volume-based' && (
              <Form.Group className="mb-3">
                <Form.Label>Volume Threshold</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="1000"
                  value={newSchedule.volumeThreshold}
                  onChange={(e) => setNewSchedule({...newSchedule, volumeThreshold: parseInt(e.target.value)})}
                />
              </Form.Group>
            )}
            {newSchedule.rotationType === 'health-based' && (
              <Form.Group className="mb-3">
                <Form.Label>Health Threshold (%)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="70"
                  value={newSchedule.healthThreshold}
                  onChange={(e) => setNewSchedule({...newSchedule, healthThreshold: parseInt(e.target.value)})}
                />
              </Form.Group>
            )}
              <Form.Group className="mb-3">
                <Form.Label>Select Email Accounts</Form.Label>
                {accounts.length === 0 ? (
                  <Alert variant="info">
                    No email accounts available. Please create email accounts first on the Email Accounts page.
                  </Alert>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {accounts.map(account => (
                      <Form.Check
                        key={account.id}
                        type="checkbox"
                        id={`account-${account.id}`}
                        label={account.email || `${account.name}@${account.domain}`}
                        checked={newSchedule.selectedAccounts.includes(account.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewSchedule({
                              ...newSchedule,
                              selectedAccounts: [...newSchedule.selectedAccounts, account.id]
                            });
                          } else {
                            setNewSchedule({
                              ...newSchedule,
                              selectedAccounts: newSchedule.selectedAccounts.filter(id => id !== account.id)
                            });
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
              </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="enabled"
                label="Enable schedule immediately"
                checked={newSchedule.enabled}
                onChange={(e) => setNewSchedule({...newSchedule, enabled: e.target.checked})}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateSchedule} disabled={loading}>
            {loading ? 'Creating...' : 'Create Schedule'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Schedule Modal */}
      <Modal show={showEditModal} onHide={() => {
        setShowEditModal(false);
        setEditingSchedule(null);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Rotation Schedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingSchedule ? (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Schedule Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Daily Marketing Rotation"
                      value={editingSchedule.name || ''}
                      onChange={(e) => setEditingSchedule({...editingSchedule, name: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rotation Type</Form.Label>
                    <Form.Select
                      value={editingSchedule.rotationType || 'round-robin'}
                      onChange={(e) => setEditingSchedule({...editingSchedule, rotationType: e.target.value})}
                    >
                      <option value="round-robin">Round Robin</option>
                      <option value="time-based">Time-Based</option>
                      <option value="volume-based">Volume-Based</option>
                      <option value="health-based">Health-Based</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Describe the purpose of this rotation schedule"
                  value={editingSchedule.description || ''}
                  onChange={(e) => setEditingSchedule({...editingSchedule, description: e.target.value})}
                />
              </Form.Group>
              {editingSchedule.rotationType === 'time-based' && (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Rotation Interval (hours)</Form.Label>
                      <Form.Control
                        type="number"
                        value={editingSchedule.interval || 24}
                        onChange={(e) => setEditingSchedule({...editingSchedule, interval: parseInt(e.target.value)})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Timezone</Form.Label>
                      <Form.Select
                        value={editingSchedule.timezone || 'UTC'}
                        onChange={(e) => setEditingSchedule({...editingSchedule, timezone: e.target.value})}
                      >
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">Eastern Time</option>
                        <option value="America/Chicago">Central Time</option>
                        <option value="America/Denver">Mountain Time</option>
                        <option value="America/Los_Angeles">Pacific Time</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              )}
              {editingSchedule.rotationType === 'volume-based' && (
                <Form.Group className="mb-3">
                  <Form.Label>Volume Threshold</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="1000"
                    value={editingSchedule.volumeThreshold || 1000}
                    onChange={(e) => setEditingSchedule({...editingSchedule, volumeThreshold: parseInt(e.target.value)})}
                  />
                </Form.Group>
              )}
              {editingSchedule.rotationType === 'health-based' && (
                <Form.Group className="mb-3">
                  <Form.Label>Health Threshold (%)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="70"
                    value={editingSchedule.healthThreshold || 70}
                    onChange={(e) => setEditingSchedule({...editingSchedule, healthThreshold: parseInt(e.target.value)})}
                  />
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Select Email Accounts</Form.Label>
                {accounts.length === 0 ? (
                  <Alert variant="info">
                    No email accounts available. Please create email accounts first on the Email Accounts page.
                  </Alert>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {accounts.map(account => (
                      <Form.Check
                        key={account.id}
                        type="checkbox"
                        id={`edit-account-${account.id}`}
                        label={account.email || `${account.name}@${account.domain}`}
                        checked={(editingSchedule.selectedAccounts || []).includes(account.id)}
                        onChange={(e) => {
                          const currentAccounts = editingSchedule.selectedAccounts || [];
                          if (e.target.checked) {
                            setEditingSchedule({
                              ...editingSchedule,
                              selectedAccounts: [...currentAccounts, account.id]
                            });
                          } else {
                            setEditingSchedule({
                              ...editingSchedule,
                              selectedAccounts: currentAccounts.filter(id => id !== account.id)
                            });
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="edit-enabled"
                  label="Enable schedule"
                  checked={editingSchedule.enabled || false}
                  onChange={(e) => setEditingSchedule({...editingSchedule, enabled: e.target.checked})}
                />
              </Form.Group>
            </Form>
          ) : (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" className="text-primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowEditModal(false);
            setEditingSchedule(null);
          }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateSchedule} disabled={loading || !editingSchedule}>
            {loading ? 'Updating...' : 'Update Schedule'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Help Sidebar */}
      <HelpSidebar 
        pageKey="rotation"
        isOpen={showHelpSidebar}
        onToggle={() => setShowHelpSidebar(!showHelpSidebar)}
      />
    </Container>
    </>
  );
};

export default AccountRotationScheduling;
